/* eslint-disable react-refresh/only-export-components */
import { AiCreationParams } from '@/manager/manager/agent-user-messages-manager';
import { SORA2, SORA2_PRO, KLING_V26_PRO, NANO_BANANA, NANO_BANANA_PRO, SEEDREAM_V45 } from './ai-creation-model';
import { LIVE_SCRIPT_AUDIO_PREVIEW_COUNT } from '.';

/**
 * 积分事件类型定义
 * 对应后端 estimateAll 的 event 字段
 */
export const CREDIT_EVENTS = {
  // 工作流类型
  VIDEO: 'miuta_agent_make_clone_short_video',
  PODCAST_CUSTOM: 'miuta_agent_make_clone_pod_cast_custom',
  PODCAST_ZH: 'miuta_agent_make_clone_pod_cast_zh',
  PODCAST_EN: 'miuta_agent_make_clone_pod_cast_en',
  PODCAST_DEFAULT: 'miuta_agent_make_clone_pod_cast',
  MOMENTS: 'miuta_agent_make_clone_friend_circle',
  GRAPHIC: 'miuta_agent_make_clone_picture_text',
  ARTICLE: 'miuta_agent_make_clone_article',
  /** 播客口播：120 字符阶梯 * 8积分 */
  PODCAST_SPEECH_120_CHR: 'miuta_agent_make_clone_pod_cast_speech_120chr',
  /** 短视频口播：120 字符阶梯 * 13积分 */
  SHORT_SPEECH_120_CHR: 'miuta_agent_make_clone_short_speech_120chr',
  /** 短视频非口播：15 秒阶梯 * 13积分 */
  SHORT_DURATION_15S: 'miuta_agent_make_clone_short_duration_15s',
  /** 音频预览：1000 字符阶梯 * 基础积分 */
  AUDIO_TTS_PREVIEW_1000_CHR: 'miuta_audio_tts_1000chr',

  // AI创作 - Sora2
  SORA2_4S: 'miuta_agent_ai_make_sora2_4',
  SORA2_8S: 'miuta_agent_ai_make_sora2_8',
  SORA2_12S: 'miuta_agent_ai_make_sora2_12',

  // AI创作 - Sora2 Pro
  SORA2_PRO_720P_4S: 'miuta_agent_ai_make_sora2_pro_720p_4',
  SORA2_PRO_720P_8S: 'miuta_agent_ai_make_sora2_pro_720p_8',
  SORA2_PRO_720P_12S: 'miuta_agent_ai_make_sora2_pro_720p_12',
  SORA2_PRO_1080P_4S: 'miuta_agent_ai_make_sora2_pro_1080p_4',
  SORA2_PRO_1080P_8S: 'miuta_agent_ai_make_sora2_pro_1080p_8',
  SORA2_PRO_1080P_12S: 'miuta_agent_ai_make_sora2_pro_1080p_12',

  // AI创作 - Kling
  KLING_5S: 'miuta_agent_ai_make_kling_2_6_pro_5s',
  KLING_10S: 'miuta_agent_ai_make_kling_2_6_pro_10s',

  // AI创作 - Nano Banana
  NANO_BANANA_1K: 'miuta_agent_ai_make_nano_banana_1k',
  NANO_BANANA_2K: 'miuta_agent_ai_make_nano_banana_2k',
  NANO_BANANA_PRO_1K: 'miuta_agent_ai_make_nano_banana_pro_1k',
  NANO_BANANA_PRO_2K: 'miuta_agent_ai_make_nano_banana_pro_2k',
  NANO_BANANA_PRO_4K: 'miuta_agent_ai_make_nano_banana_pro_4k',

  // AI创作 - Seedream
  SEEDREAM_2K: 'miuta_agent_ai_make_seedream_2k',
  SEEDREAM_4K: 'miuta_agent_ai_make_seedream_4k',
} as const;

/**
 * 积分映射表类型
 */
export type CreditsMapping = Record<string, number>;

/**
 * 默认积分配置（降级方案，当 estimateAll 不可用时使用）
 */
export const DEFAULT_CREDITS_CONFIG: CreditsMapping = {
  // 工作流类型
  [CREDIT_EVENTS.VIDEO]: 50,
  [CREDIT_EVENTS.PODCAST_CUSTOM]: 250,
  [CREDIT_EVENTS.PODCAST_ZH]: 180,
  [CREDIT_EVENTS.PODCAST_EN]: 250,
  [CREDIT_EVENTS.PODCAST_DEFAULT]: 220,
  [CREDIT_EVENTS.MOMENTS]: 30,
  [CREDIT_EVENTS.GRAPHIC]: 80,
  [CREDIT_EVENTS.ARTICLE]: 80,
  [CREDIT_EVENTS.PODCAST_SPEECH_120_CHR]: 8,
  [CREDIT_EVENTS.SHORT_SPEECH_120_CHR]: 13,
  [CREDIT_EVENTS.SHORT_DURATION_15S]: 13,
  [CREDIT_EVENTS.AUDIO_TTS_PREVIEW_1000_CHR]: 3,

  // AI创作 - Sora2
  [CREDIT_EVENTS.SORA2_4S]: 250,
  [CREDIT_EVENTS.SORA2_8S]: 400,
  [CREDIT_EVENTS.SORA2_12S]: 600,

  // AI创作 - Sora2 Pro
  [CREDIT_EVENTS.SORA2_PRO_720P_4S]: 500,
  [CREDIT_EVENTS.SORA2_PRO_720P_8S]: 800,
  [CREDIT_EVENTS.SORA2_PRO_720P_12S]: 900,
  [CREDIT_EVENTS.SORA2_PRO_1080P_4S]: 800,
  [CREDIT_EVENTS.SORA2_PRO_1080P_8S]: 900,
  [CREDIT_EVENTS.SORA2_PRO_1080P_12S]: 1500,

  // AI创作 - Kling
  [CREDIT_EVENTS.KLING_5S]: 250,
  [CREDIT_EVENTS.KLING_10S]: 500,

  // AI创作 - Nano Banana
  [CREDIT_EVENTS.NANO_BANANA_1K]: 20,
  [CREDIT_EVENTS.NANO_BANANA_2K]: 20,
  [CREDIT_EVENTS.NANO_BANANA_PRO_1K]: 60,
  [CREDIT_EVENTS.NANO_BANANA_PRO_2K]: 60,
  [CREDIT_EVENTS.NANO_BANANA_PRO_4K]: 120,

  // AI创作 - Seedream
  [CREDIT_EVENTS.SEEDREAM_2K]: 20,
  [CREDIT_EVENTS.SEEDREAM_4K]: 20,
};

/**
 * 全局积分映射表
 * 初始化时使用默认配置
 * 从后端 estimateAll 数据初始化
 */
let globalCreditsMapping: CreditsMapping = { ...DEFAULT_CREDITS_CONFIG };

/**
 * 初始化积分映射表
 * @param estimateAll 后端返回的积分配置数组
 */
export const initCreditsMapping = (estimateAll: Array<{ event: string; estimate: number }> | null | undefined) => {
  if (!estimateAll || !Array.isArray(estimateAll)) {
    console.warn('[CreditsMapping] estimateAll is invalid, using default config');
    return;
  }

  const mapping: CreditsMapping = {};

  for (const item of estimateAll) {
    if (item.event && typeof item.estimate === 'number') {
      mapping[item.event] = item.estimate;
    }
  }

  globalCreditsMapping = mapping;
  console.log('[CreditsMapping] Initialized with', Object.keys(mapping).length, 'events');
};

/**
 * 获取指定事件的积分
 * @param event 事件名称
 * @returns 积分数，如果未找到返回 0
 */
export const getCreditByEvent = (event: string): number => {
  return globalCreditsMapping[event] ?? 0;
};

/**
 * 获取工作流类型的积分
 * @param inputType 输入类型 (1:视频, 2:播客, 3:朋友圈, 4:图文, 5:文章)
 * @param isScriptLive 是否为短视频/播客的口播模式
 * @param outLang 输出语言 (zh-CN, zh-HK, en-US)
 * @returns 积分数
 */
export const getWorkflowCredits = (
  inputType: number,
  isScriptLive: boolean = false,
  outLang?: string,
  options?: {
    /** 输入内容 120 字符阶梯数 */
    contentSteps?: number;
    /** 视频时长 15 秒阶梯数 */
    durationSteps?: number;
  }
): number => {
  const contentSteps = Math.max(1, options?.contentSteps ?? 1);
  const durationSteps = Math.max(1, options?.durationSteps ?? 1);

  switch (inputType) {
    case 1: // 视频
      if (isScriptLive) {
        return contentSteps * getCreditByEvent(CREDIT_EVENTS.SHORT_SPEECH_120_CHR);
      }
      return durationSteps * getCreditByEvent(CREDIT_EVENTS.SHORT_DURATION_15S);
    case 2: // 播客
      if (isScriptLive) {
        return contentSteps * getCreditByEvent(CREDIT_EVENTS.PODCAST_SPEECH_120_CHR);
      }
      switch (outLang) {
        case 'zh-CN':
        case 'zh-HK':
          return getCreditByEvent(CREDIT_EVENTS.PODCAST_ZH);
        case 'en-US':
          return getCreditByEvent(CREDIT_EVENTS.PODCAST_EN);
        default:
          return getCreditByEvent(CREDIT_EVENTS.PODCAST_DEFAULT);
      }
    case 3: // 朋友圈
      return getCreditByEvent(CREDIT_EVENTS.MOMENTS);
    case 4: // 图文
      return getCreditByEvent(CREDIT_EVENTS.GRAPHIC);
    case 5: // 文章
      return getCreditByEvent(CREDIT_EVENTS.ARTICLE);
    default:
      return 0;
  }
};

/**
 * AI创作模型积分策略映射
 * 根据模型和参数生成对应的事件
 * 视频模型：直接返回事件名，积分固定
 * 图片模型：返回事件名，积分需要乘以数量
 */
const AI_CREATION_EVENT_STRATEGY: Record<string, (params: AiCreationParams) => string> = {
  [SORA2]: (params) => {
    const duration = params.duration || '8';
    switch (duration) {
      case '4': return CREDIT_EVENTS.SORA2_4S;
      case '8': return CREDIT_EVENTS.SORA2_8S;
      case '12': return CREDIT_EVENTS.SORA2_12S;
      default: return CREDIT_EVENTS.SORA2_8S;
    }
  },
  [SORA2_PRO]: (params) => {
    const imageSize = params.imageSize || '720p';
    const duration = params.duration || '8';

    if (imageSize === '720p') {
      switch (duration) {
        case '4': return CREDIT_EVENTS.SORA2_PRO_720P_4S;
        case '8': return CREDIT_EVENTS.SORA2_PRO_720P_8S;
        case '12': return CREDIT_EVENTS.SORA2_PRO_720P_12S;
        default: return CREDIT_EVENTS.SORA2_PRO_720P_8S;
      }
    } else {
      switch (duration) {
        case '4': return CREDIT_EVENTS.SORA2_PRO_1080P_4S;
        case '8': return CREDIT_EVENTS.SORA2_PRO_1080P_8S;
        case '12': return CREDIT_EVENTS.SORA2_PRO_1080P_12S;
        default: return CREDIT_EVENTS.SORA2_PRO_1080P_8S;
      }
    }
  },
  [KLING_V26_PRO]: (params) => {
    const duration = params.duration || '5';
    return duration === '10' ? CREDIT_EVENTS.KLING_10S : CREDIT_EVENTS.KLING_5S;
  },
  [NANO_BANANA]: (params) => {
    // NANO_BANANA 模型不支持选择分辨率，只有固定的 2K 积分
    return CREDIT_EVENTS.NANO_BANANA_2K;
  },
  [NANO_BANANA_PRO]: (params) => {
    const imageSize = params.imageSize || '2K';
    if (imageSize === '1K') return CREDIT_EVENTS.NANO_BANANA_PRO_1K;
    if (imageSize === '4K') return CREDIT_EVENTS.NANO_BANANA_PRO_4K;
    return CREDIT_EVENTS.NANO_BANANA_PRO_2K;
  },
  [SEEDREAM_V45]: (params) => {
    const imageSize = params.imageSize || '2K';
    return imageSize === '4K' ? CREDIT_EVENTS.SEEDREAM_4K : CREDIT_EVENTS.SEEDREAM_2K;
  },
};

/**
 * 图片模型列表（这些模型的积分需要乘以数量）
 */
const IMAGE_MODELS = [NANO_BANANA, NANO_BANANA_PRO, SEEDREAM_V45];

/**
 * 判断是否为图片模型
 */
const isImageModel = (model: string): boolean => {
  return IMAGE_MODELS.includes(model);
};

/**
 * 获取 AI 创作模型的积分
 * @param model 模型名称
 * @param params 模型参数
 * @returns 积分数
 * 
 * 视频模型：直接返回事件对应的积分
 * 图片模型：返回单张图片积分 × 数量
 */
export const getAiCreationCredits = (model: string, params: AiCreationParams): number => {
  const strategy = AI_CREATION_EVENT_STRATEGY[model];
  if (!strategy) {
    console.warn(`[CreditsMapping] Unknown model: ${model}`);
    return 0;
  }

  const event = strategy(params);
  const singleCredit = getCreditByEvent(event);

  if (singleCredit === 0) {
    return 0;
  }

  // 如果是图片模型，需要乘以数量
  if (isImageModel(model)) {
    const count = parseInt(params.count || '1', 10);
    return singleCredit * count;
  }

  // 视频模型直接返回积分
  return singleCredit;
};

/**
 * 获取口播音频预览积分
 * 规则：1000 字符阶梯 * 接口返回基础积分（event: miuta_audio_tts_1000chr）
 */
export const getAudioPreviewCredits = (contentLength: number): number => {
  const contentSteps = Math.max(1, Math.ceil(Math.max(contentLength, 1) / LIVE_SCRIPT_AUDIO_PREVIEW_COUNT));
  return contentSteps * getCreditByEvent(CREDIT_EVENTS.AUDIO_TTS_PREVIEW_1000_CHR);
};

/**
 * 向后兼容的积分映射表（用于保持旧代码兼容性）
 */
export const CREDITS_MAP = {
  1: getWorkflowCredits(1),
  2: getWorkflowCredits(2),
  3: getWorkflowCredits(3),
  4: getWorkflowCredits(4),
  5: getWorkflowCredits(5),
};

export const PODCAST_CREDITS = {
  'zh-CN': getWorkflowCredits(2, false, 'zh-CN'),
  'zh-HK': getWorkflowCredits(2, false, 'zh-HK'),
  'en-US': getWorkflowCredits(2, false, 'en-US'),
};

export const PODCAST_CREDITS_LIVE = getWorkflowCredits(2, true);